import {
    addBlockTypeCommand,
    blockquoteSchema,
    bulletListSchema,
    clearTextInCurrentBlockCommand,
    codeBlockSchema,
    headingSchema,
    hrSchema,
    listItemSchema,
    orderedListSchema,
    selectTextNearPosCommand,
    setBlockTypeCommand,
    toggleEmphasisCommand,
    toggleInlineCodeCommand,
    toggleStrongCommand,
    wrapInBlockTypeCommand,
} from '@milkdown/kit/preset/commonmark'
import { createTable, toggleStrikethroughCommand } from '@milkdown/kit/preset/gfm'
import { commandsCtx, editorViewCtx } from '@milkdown/kit/core'
import {
    BoldIcon,
    CodeSquareIcon,
    Heading1Icon,
    Heading2Icon,
    Heading3Icon,
    HighlighterIcon,
    ImagePlusIcon,
    ItalicIcon,
    ListIcon,
    ListOrderedIcon,
    ListTodoIcon,
    Link2Icon,
    MinusIcon,
    QuoteIcon,
    StrikethroughIcon,
    Table2Icon,
    type LucideIcon,
} from 'lucide-react'
import { toggleLinkCommand } from '@milkdown/kit/component/link-tooltip'

import { RichTextEditorCopy } from '@/components/common/RichTextEditor/copy'
import type { RichTextEditorMode } from '@/components/common/RichTextEditor/types'

import type { Ctx } from '@milkdown/kit/ctx'

export type RichTextEditorActionId =
    | 'bold'
    | 'italic'
    | 'strike'
    | 'inlineCode'
    | 'link'
    | 'h1'
    | 'h2'
    | 'h3'
    | 'quote'
    | 'divider'
    | 'bulletList'
    | 'orderedList'
    | 'taskList'
    | 'image'
    | 'codeBlock'
    | 'table'

export type RichTextEditorActionGroup = 'inline' | 'text' | 'lists' | 'other'

export type RichTextEditorActionDefinition = {
    id: RichTextEditorActionId
    group: RichTextEditorActionGroup
    icon: LucideIcon
    label: string
}

type RichTextEditorActionOptions = {
    codeBlock: boolean
    copy: RichTextEditorCopy
    headings: boolean
    imageUploadEnabled: boolean
    mode?: RichTextEditorMode
    quote: boolean
    taskList: boolean
}

const richInlineActionIds = new Set<RichTextEditorActionId>(['bold', 'italic', 'strike', 'inlineCode', 'link'])

export const getRichTextEditorActionDefinitions = ({
    codeBlock,
    copy,
    headings,
    imageUploadEnabled,
    mode,
    quote,
    taskList,
}: RichTextEditorActionOptions): RichTextEditorActionDefinition[] => {
    const actions: RichTextEditorActionDefinition[] = [
        {
            id: 'bold',
            group: 'inline',
            icon: BoldIcon,
            label: copy.toolbar.bold,
        },
        {
            id: 'italic',
            group: 'inline',
            icon: ItalicIcon,
            label: copy.toolbar.italic,
        },
        {
            id: 'strike',
            group: 'inline',
            icon: StrikethroughIcon,
            label: copy.toolbar.strikethrough,
        },
        {
            id: 'inlineCode',
            group: 'inline',
            icon: HighlighterIcon,
            label: copy.toolbar.highlight,
        },
        {
            id: 'link',
            group: 'inline',
            icon: Link2Icon,
            label: copy.toolbar.link,
        },
        ...(headings
            ? ([
                  {
                      id: 'h1',
                      group: 'text',
                      icon: Heading1Icon,
                      label: copy.blocks.h1,
                  },
                  {
                      id: 'h2',
                      group: 'text',
                      icon: Heading2Icon,
                      label: copy.blocks.h2,
                  },
                  {
                      id: 'h3',
                      group: 'text',
                      icon: Heading3Icon,
                      label: copy.blocks.h3,
                  },
              ] satisfies RichTextEditorActionDefinition[])
            : []),
        ...(quote
            ? ([
                  {
                      id: 'quote',
                      group: 'text',
                      icon: QuoteIcon,
                      label: copy.blocks.quote,
                  },
              ] satisfies RichTextEditorActionDefinition[])
            : []),
        {
            id: 'bulletList',
            group: 'lists',
            icon: ListIcon,
            label: copy.blocks.bulletList,
        },
        {
            id: 'orderedList',
            group: 'lists',
            icon: ListOrderedIcon,
            label: copy.blocks.orderedList,
        },
        ...(taskList
            ? ([
                  {
                      id: 'taskList',
                      group: 'lists',
                      icon: ListTodoIcon,
                      label: copy.blocks.taskList,
                  },
              ] satisfies RichTextEditorActionDefinition[])
            : []),
        ...(imageUploadEnabled
            ? ([
                  {
                      id: 'image',
                      group: 'other',
                      icon: ImagePlusIcon,
                      label: copy.blocks.image,
                  },
              ] satisfies RichTextEditorActionDefinition[])
            : []),
        {
            id: 'divider',
            group: 'other',
            icon: MinusIcon,
            label: copy.blocks.divider,
        },
        {
            id: 'table',
            group: 'other',
            icon: Table2Icon,
            label: copy.blocks.table,
        },
        ...(codeBlock
            ? ([
                  {
                      id: 'codeBlock',
                      group: 'other',
                      icon: CodeSquareIcon,
                      label: copy.blocks.codeBlock,
                  },
              ] satisfies RichTextEditorActionDefinition[])
            : []),
    ]

    if (mode === 'rich-inline') {
        return actions.filter((action) => richInlineActionIds.has(action.id))
    }

    return actions
}

export const executeRichTextEditorAction = (
    actionId: RichTextEditorActionId,
    ctx: Ctx,
    { openImagePicker }: { openImagePicker: () => void }
) => {
    const commands = ctx.get(commandsCtx)

    switch (actionId) {
        case 'bold':
            commands.call(toggleStrongCommand.key)
            return
        case 'italic':
            commands.call(toggleEmphasisCommand.key)
            return
        case 'strike':
            commands.call(toggleStrikethroughCommand.key)
            return
        case 'inlineCode':
            commands.call(toggleInlineCodeCommand.key)
            return
        case 'link':
            commands.call(toggleLinkCommand.key)
            return
        case 'h1':
        case 'h2':
        case 'h3': {
            const heading = headingSchema.type(ctx)
            const level = Number(actionId.slice(1))

            commands.call(clearTextInCurrentBlockCommand.key)
            commands.call(setBlockTypeCommand.key, {
                nodeType: heading,
                attrs: { level },
            })
            return
        }
        case 'quote': {
            const blockquote = blockquoteSchema.type(ctx)

            commands.call(clearTextInCurrentBlockCommand.key)
            commands.call(wrapInBlockTypeCommand.key, { nodeType: blockquote })
            return
        }
        case 'divider': {
            const divider = hrSchema.type(ctx)

            commands.call(clearTextInCurrentBlockCommand.key)
            commands.call(addBlockTypeCommand.key, { nodeType: divider })
            return
        }
        case 'bulletList': {
            const bulletList = bulletListSchema.type(ctx)

            commands.call(clearTextInCurrentBlockCommand.key)
            commands.call(wrapInBlockTypeCommand.key, { nodeType: bulletList })
            return
        }
        case 'orderedList': {
            const orderedList = orderedListSchema.type(ctx)

            commands.call(clearTextInCurrentBlockCommand.key)
            commands.call(wrapInBlockTypeCommand.key, { nodeType: orderedList })
            return
        }
        case 'taskList': {
            const listItem = listItemSchema.type(ctx)

            commands.call(clearTextInCurrentBlockCommand.key)
            commands.call(wrapInBlockTypeCommand.key, {
                nodeType: listItem,
                attrs: { checked: false },
            })
            return
        }
        case 'image':
            openImagePicker()
            return
        case 'codeBlock': {
            const codeBlock = codeBlockSchema.type(ctx)

            commands.call(clearTextInCurrentBlockCommand.key)
            commands.call(setBlockTypeCommand.key, { nodeType: codeBlock })
            return
        }
        case 'table': {
            const { from } = ctx.get(editorViewCtx).state.selection

            commands.call(clearTextInCurrentBlockCommand.key)
            commands.call(addBlockTypeCommand.key, { nodeType: createTable(ctx, 3, 3) })
            commands.call(selectTextNearPosCommand.key, { pos: from })
            return
        }
    }
}
